import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TicketPercent } from 'lucide-react';
import { Button } from 'components/ui/button';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { formatNumber } from 'features/usage/utils/usage-format';
import { usePreviewCoupon } from '../hooks/use-credits';
import { creditsErrorCode } from '../services/credits-service';
import type { CouponQuote } from '../types';
import { couponBonusCredits } from '../utils/amount';
import { couponErrorTexts } from '../utils/coupon-errors';
import { COUPON_CODE_LENGTH, CouponCodeInput } from './coupon-code-input';

interface CouponRedeemProps {
  /** The amount picked above, in CHF. The coupon is worked out on it. */
  amountChf: number | null;
  /** Lets the applied coupon say how many bonus credits it adds. */
  creditsPerChf?: number;
  disabled?: boolean;
  /** The coupon already applied to this amount, if any. */
  applied?: CouponQuote | null;
  onQuote: (code: string, quote: CouponQuote) => void;
  onClear: () => void;
  /** How the collapsed link looks: a sentence with "Click here", or a ticket icon button. */
  triggerStyle?: 'inline' | 'icon';
}

/** Figma 10827:22370 (entering a code) and 10827:22800 (applied). */
export const CouponRedeem = ({
  amountChf,
  creditsPerChf,
  disabled,
  applied,
  onQuote,
  onClear,
  triggerStyle = 'inline',
}: CouponRedeemProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [open, setOpen] = useState(false);
  const { mutate: preview, isPending, error, reset } = usePreviewCoupon();
  const canRedeem = code.length === COUPON_CODE_LENGTH && amountChf !== null && !isPending && !disabled;
  const failure = error
    ? (couponErrorTexts(t)[creditsErrorCode(error)] ?? t('COUPON_CHECK_FAILED', 'The coupon could not be checked. Try again.'))
    : '';

  const redeem = () => {
    if (amountChf === null) return;
    preview({ amountChf, couponCode: code }, { onSuccess: (quote) => onQuote(code, quote) });
  };

  // Closing also drops any applied coupon, since the collapsed link can't show one.
  const close = () => {
    setCode('');
    reset();
    onClear();
    setOpen(false);
  };

  if (applied) {
    const bonus = amountChf !== null && creditsPerChf ? couponBonusCredits(applied.credits, amountChf, creditsPerChf) : 0;
    let effect = '';
    if (bonus > 0) {
      effect = t('COUPON_BONUS_CREDITS', { credits: formatNumber(bonus), defaultValue: '{{credits}} bonus credits' });
    } else if (applied.discountMinor > 0) {
      effect = t('COUPON_YOU_SAVE', {
        amount: formatMinor(applied.discountMinor, applied.currencyCode),
        defaultValue: 'you save {{amount}}',
      });
    }

    return (
      <div role="status" className="flex items-center gap-3 rounded-card bg-success-50 px-3.5 py-2.5 dark:bg-success-700/20">
        <TicketPercent className="h-4 w-4 shrink-0 text-success-700 dark:text-success-50" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm text-grey-950 dark:text-foreground">
          <span className="font-medium">{applied.couponCode || code}</span>{' '}
          {t('COUPON_APPLIED_LABEL', 'applied')}
          {effect && ` · ${effect}`}
        </p>
        <button
          type="button"
          className="shrink-0 rounded-sm text-sm font-medium text-grey-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:text-foreground"
          disabled={disabled}
          onClick={close}
        >
          {t('REMOVE', 'Remove')}
        </button>
      </div>
    );
  }

  if (!open && triggerStyle === 'icon') {
    return (
      <button
        type="button"
        className="flex w-fit items-center gap-2 rounded-sm text-sm font-medium leading-body text-grey-900 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <TicketPercent className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('HAVE_A_COUPON', 'Have a coupon?')}
      </button>
    );
  }

  if (!open) {
    return (
      <p className="-mb-1 text-sm text-foreground">
        {t('HAVE_A_COUPON', 'Have a coupon?')}{' '}
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm text-foreground underline"
          onClick={() => setOpen(true)}
        >
          {t('CLICK_HERE', 'Click here')}
        </Button>
      </p>
    );
  }

  return (
    <section className="flex flex-col gap-2" aria-labelledby="coupon-redeem-title">
      <div className="flex items-end justify-between gap-3">
        <h3 id="coupon-redeem-title" className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
          {t('COUPON_CODE_LABEL', 'Coupon code')}
        </h3>
        <button
          type="button"
          className="rounded-sm text-xs leading-heading text-grey-600 hover:text-grey-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground"
          disabled={isPending}
          onClick={close}
        >
          {t('CANCEL', 'Cancel')}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <CouponCodeInput
          value={code}
          invalid={!!failure}
          disabled={disabled || isPending}
          onChange={(next) => {
            reset();
            setCode(next);
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-full border-grey-100 bg-background px-5 text-sm font-medium text-grey-900 shadow-none hover:bg-grey-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          disabled={!canRedeem}
          loading={isPending}
          onClick={redeem}
        >
          {t('REDEEM', 'Redeem')}
        </Button>
      </div>

      {failure && (
        <p role="alert" className="text-xs leading-heading text-error-700 dark:text-error-500">
          {failure}
        </p>
      )}
    </section>
  );
};
